/**
 * Safe sessionStorage / localStorage helpers.
 *
 * Why this exists: every call site that touches `sessionStorage` had a
 * `try { … } catch { /* noop *\/ }` wrapper because Safari's private
 * browsing mode (and some embedded webviews) throws on `setItem`. The
 * code-review flagged those empty catches as a smell. Centralising them
 * here:
 *   • makes the intent obvious (storage may legitimately fail — that's
 *     OK, the app still works without persistence),
 *   • logs once per failure in dev mode so regressions are visible,
 *   • shrinks 6+ call sites to a single import.
 *
 * Returns sensible defaults (true / null / false) so callers never have
 * to defensively check for storage availability themselves.
 */

const isDev = process.env.NODE_ENV === 'development';

function _logFailure(op, key, err) {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn(`[safe-storage] ${op}(${key}) failed:`, err?.message || err);
  }
}

/** Read a value from sessionStorage. Returns `null` if missing or storage unavailable. */
export function safeSessionGet(key) {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
  } catch (err) {
    _logFailure('sessionGet', key, err);
    return null;
  }
}

/** Write a value to sessionStorage. Returns `true` on success, `false` if storage unavailable. */
export function safeSessionSet(key, value) {
  try {
    if (typeof window === 'undefined') return false;
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (err) {
    _logFailure('sessionSet', key, err);
    return false;
  }
}

/** Remove a key from sessionStorage. Always safe to call; returns true on success. */
export function safeSessionRemove(key) {
  try {
    if (typeof window === 'undefined') return false;
    window.sessionStorage.removeItem(key);
    return true;
  } catch (err) {
    _logFailure('sessionRemove', key, err);
    return false;
  }
}

/** Parse a JSON value from sessionStorage. Returns `null` if missing, unavailable, or unparseable. */
export function safeSessionGetJSON(key) {
  const raw = safeSessionGet(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    _logFailure('sessionGetJSON', key, err);
    return null;
  }
}

/** Stringify and store a JSON value. Returns `true` on success. */
export function safeSessionSetJSON(key, value) {
  try {
    return safeSessionSet(key, JSON.stringify(value));
  } catch (err) {
    _logFailure('sessionSetJSON', key, err);
    return false;
  }
}
